import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { CATEGORY_PILL_CLASS, CATEGORY_PILL_FRAME } from "../../../shared/ui/category-pill";
import { pewterFrame, pewterInner, solid } from "../../../shared/ui/pewter-edge";
import { useWomenTripsSoft } from "../hooks/use-women-trips";
import { MUTED, NAVY, WOMEN_INK, WOMEN_PINK, WOMEN_WASH } from "./kit";

/**
 * The two ways in from the home page (lab 01, owner picked both): a chip at
 * the end of the category row, and a pink banner between the categories and
 * the cities. Both show only when there is a trip coming up, so the home page
 * never points at an empty hub.
 */

const CHIP_INNER = pewterInner(solid(WOMEN_WASH), 999, { gap: 3 });

/** True once the trips are known and at least one is coming up. */
function useHasTrips(lang: string): { count: number; image: string | null } | null {
  const { data } = useWomenTripsSoft(lang);
  const trips = Array.isArray(data) ? data : [];
  if (trips.length === 0) return null;
  return { count: trips.length, image: trips.find((x) => x.imageUrl)?.imageUrl ?? null };
}

/** "Women only" at the end of the category row, sized like the category pills. */
export function WomenCategoryChip({ lang }: { lang: string }) {
  const { t } = useTranslation();
  if (!useHasTrips(lang)) return null;
  // Its own list item, so the row has no empty slot while no trip is coming up.
  return (
    <li>
    <Link
      to="/$lang/women"
      params={{ lang }}
      // The guest's query travels with her (state preservation).
      search={(prev: Record<string, unknown>) => prev}
      className={CATEGORY_PILL_CLASS}
      style={CATEGORY_PILL_FRAME}
      data-testid="women-category-chip"
    >
      <span className="flex min-h-[62px] items-center gap-2.5 py-2 pe-5 ps-2.5" style={CHIP_INNER}>
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/70">
          <Icon name="ri-user-heart-line" className="text-[22px]" style={{ color: WOMEN_PINK }} aria-hidden />
        </span>
        <span className="font-semibold text-[15px] leading-[21px]" style={{ color: WOMEN_INK }}>
          {t("women.tag")}
        </span>
      </span>
    </Link>
    </li>
  );
}

const BANNER_R = 22;
const BANNER_FRAME = pewterFrame(BANNER_R);
const BANNER_INNER = pewterInner(
  "linear-gradient(160deg, #F4C0D1 0%, #E58FB0 55%, #B8336A 100%)",
  BANNER_R,
  { hairline: "rgba(255,255,255,0.18)" },
);

/** The pink card between the categories and the cities. */
export function WomenHomeBanner({ lang }: { lang: string }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const info = useHasTrips(lang);
  if (!info) return null;
  return (
    <section className="relative mx-auto w-full max-w-[1320px] px-4 py-3 sm:px-6 md:py-4 lg:px-8 lg:py-5">
      <Link
        to="/$lang/women"
        params={{ lang }}
        // The guest's query travels with her (state preservation).
        search={(prev: Record<string, unknown>) => prev}
        className="group block transition-transform duration-200 hover:-translate-y-[2px]"
        style={{ ...BANNER_FRAME, boxShadow: `${BANNER_FRAME.boxShadow}, 0 10px 26px -12px rgba(114,36,62,0.35)` }}
        data-testid="women-home-banner"
      >
        <div className="relative flex min-h-[150px] flex-col justify-end overflow-hidden p-4 sm:min-h-[180px] sm:p-6" style={BANNER_INNER}>
          {info.image ? (
            <img
              src={info.image}
              alt=""
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover opacity-35 mix-blend-multiply"
            />
          ) : null}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{ background: "linear-gradient(to top, rgba(114,36,62,0.55) 0%, rgba(114,36,62,0) 70%)" }}
          />
          <div className="relative">
            <p className="font-heading text-[20px] font-bold leading-tight text-white sm:text-[26px]">{t("women.hub_title")}</p>
            <p className="mt-0.5 font-sans text-[13.5px] font-medium text-white/95 sm:text-[15px]">
              {t("women.home_banner_sub")}
            </p>
          </div>
        </div>
      </Link>
      <div className="mt-2 flex items-center justify-between gap-3 px-1">
        <span className="font-sans text-[13px] font-medium" style={{ color: MUTED }}>
          {t("women.home_banner_count", { count: info.count })}
        </span>
        <Link
          to="/$lang/women"
          params={{ lang }}
          search={(prev: Record<string, unknown>) => prev}
          className="inline-flex min-h-6 items-center gap-1 font-sans text-[13.5px] font-semibold"
          style={{ color: NAVY }}
        >
          {t("women.home_banner_cta")}
          <Icon name={isRtl ? "ri-arrow-left-line" : "ri-arrow-right-line"} className="text-[14px]" aria-hidden />
        </Link>
      </div>
    </section>
  );
}
